import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  TextareaAutosize,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { RichTextEditor } from '../components/RichTextEditor';
import { sanitizeTelegramHtml } from '../utils';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

interface Category {
  categoryId: number;
  name: string;
  description: string;
  hasSubcategories: boolean;
  isActive: boolean;
  index: number;
  createdAt: string;
  updatedAt: string;
}

interface Product {
  productId: number;
  name: string;
  imageUrl: string | null;
}

interface SubCategory {
  subCategoryId: number;
  name: string;
}

const CategoryEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [category, setCategory] = React.useState<Category | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const [products, setProducts] = React.useState<Product[]>([]);
  const [subCategories, setSubCategories] = React.useState<SubCategory[]>([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [total, setTotal] = React.useState(0);
  const [loadingItems, setLoadingItems] = React.useState(false);

  React.useEffect(() => {
    axios
      .get(`${API_URL}/categories/${id}`)
      .then((res) => setCategory(res.data))
      .catch((err) => {
        console.error(err);
        alert('Помилка при завантаженні категорії');
      })
      .finally(() => setLoading(false));
  }, [id]);

  React.useEffect(() => {
    if (!category) return;
    setLoadingItems(true);

    if (category.hasSubcategories) {
      axios
        .get(`${API_URL}/sub-categories`, {
          params: {
            categoryId: category.categoryId,
            page: page + 1,
            perPage: rowsPerPage,
          },
        })
        .then((res) => {
          setSubCategories(res.data.data);
          setTotal(res.data.total || res.data.data.length);
        })
        .catch((err) => console.error(err))
        .finally(() => setLoadingItems(false));
    } else {
      axios
        .get(`${API_URL}/products`, {
          params: {
            categoryId: category.categoryId,
            page: page + 1,
            limit: rowsPerPage,
          },
        })
        .then((res) => {
          setProducts(res.data.data);
          setTotal(res.data.total || res.data.data.length);
        })
        .catch((err) => console.error(err))
        .finally(() => setLoadingItems(false));
    }
  }, [category?.categoryId, category?.hasSubcategories, page, rowsPerPage]);

  if (loading) return <CircularProgress />;
  if (!category) return <Typography>Категорія не знайдена</Typography>;

  const handleChange = (field: keyof Category, value: any) => {
    setCategory((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/categories/${id}`, {
        ...category,
        description: sanitizeTelegramHtml(category.description || ''),
      });
      navigate('/categories');
    } catch (err) {
      console.error(err);
      alert('Помилка при збереженні');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Видалити категорію?')) return;
    try {
      await axios.delete(`${API_URL}/categories/${id}`);
      navigate('/categories');
    } catch (err) {
      console.error(err);
      alert('Помилка при видаленні');
    }
  };

  const handleChangePage = (_: any, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ mx: 10, mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Редагування категорії: {category.name}
      </Typography>

      <TextField
        fullWidth
        label="Назва категорії"
        value={category.name}
        onChange={(e) =>
          setCategory((prev) =>
            prev ? { ...prev, name: e.target.value } : prev,
          )
        }
        sx={{ mb: 2 }}
      />

      <RichTextEditor
        value={category.description}
        onChange={(html) =>
          setCategory((prev) => (prev ? { ...prev, description: html } : prev))
        }
        label="Опис категорії"
        minRows={4}
      />

      <TextField
        type="number"
        fullWidth
        label="Порядок відображення"
        value={category.index ?? 1}
        inputProps={{ min: 1, max: 100 }}
        onChange={(e) =>
          setCategory((prev) =>
            prev
              ? {
                  ...prev,
                  index: Math.min(100, Math.max(1, Number(e.target.value))),
                }
              : prev,
          )
        }
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography>Підкатегорії: </Typography>
        {category.hasSubcategories ? (
          <CheckCircleIcon color="success" sx={{ ml: 1 }} />
        ) : (
          <CancelIcon color="error" sx={{ ml: 1 }} />
        )}
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={!!category.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
          />
        }
        label="Показувати у боті"
        sx={{ mb: 2, display: 'block' }}
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Зберігаю...' : 'Зберегти'}
        </Button>
        <Button variant="outlined" color="error" onClick={handleDelete}>
          Видалити
        </Button>
        <Button variant="text" onClick={() => navigate('/categories')}>
          Відмінити
        </Button>
      </Box>

      <Typography variant="h5" gutterBottom>
        {category.hasSubcategories ? 'Підкатегорії' : 'Продукти категорії'}
      </Typography>

      {loadingItems ? (
        <CircularProgress />
      ) : category.hasSubcategories ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Назва</TableCell>
              <TableCell>Дія</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subCategories.map((sub) => (
              <TableRow key={sub.subCategoryId}>
                <TableCell>{sub.name}</TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      navigate(`/sub-categories/${sub.subCategoryId}`)
                    }
                  >
                    Редагувати
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Зображення</TableCell>
              <TableCell>Назва</TableCell>
              <TableCell>Дія</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.productId}>
                <TableCell>
                  {product.imageUrl ? (
                    <img
                      src={`${IMAGE_URL}${product.imageUrl}`}
                      alt={product.name}
                      style={{
                        width: 50,
                        height: 50,
                        objectFit: 'cover',
                        borderRadius: 4,
                      }}
                    />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      navigate(`/products/${product.productId}/edit`)
                    }
                  >
                    Редагувати
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 20, 50]}
      />
    </Box>
  );
};

export default CategoryEdit;
